/**
 * -----------------------------------------------------------------------------
 * Reports - Manage Sharing
 * -----------------------------------------------------------------------------
 * This page is used to list all the users of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import {
  useDeleteExternalShareEmailMutation,
  useGetExternalShareQuery,
  useGetReportSpecificFieldsQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import {
  ShareReportListItem,
  ShareReportListPayload,
} from "../../../api/types/Report";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG, REPORT_TYPES } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import { formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { FREQUENCY_OPTIONS } from "../constants";
import { useReportUtils } from "../hooks/reportUtils";
import ModalShareViaEmail from "../modalShareViaEmail";

const all_columns = ["Email", "Share Date", "Type", "Scheduled", "Frequency"];

interface LastElement {
  value: string;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}

const LastColumnElement: React.FC<LastElement> = ({
  value,
  onEditClick,
  onDeleteClick,
}) => {
  return (
    <div className="flex justify-between relative">
      <div>{value}</div>
      <div className="flex gap-4 mr-4">
        <button onClick={onEditClick} disabled={!onEditClick}>
          <img
            src={svgIcons.EditIcon}
            className={`${!onEditClick && "opacity-50 grayscale"}`}
            alt="edit"
            width={20}
            height={20}
          />
        </button>
        <button onClick={onDeleteClick} disabled={!onDeleteClick}>
          <img
            src={svgIcons.Delete}
            className={`${!onDeleteClick && "opacity-50 grayscale"}`}
            width={15}
            height={16}
          />
        </button>
      </div>
    </div>
  );
};

const ScreenReportManageSharing = () => {
  const { reportType, categoryId, reportId } = useParams<{
    reportType: string;
    categoryId: string;
    reportId: string;
  }>();
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.shareReport",
  });
  const { t: tEmail } = useTranslation("translation", {
    keyPrefix: "reports.shareViaEmail",
  });
  const { t: tMain } = useTranslation();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const { refetchCustomReportListToStore } = useReportUtils();

  const [reportParams, setReportParams] =
    React.useState<ShareReportListPayload>({
      external_share__report: reportId ?? "",
      page: 1,
      page_size: 10,
      external_share__format: "",
      ordering: "",
      search: "",
    });

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  const { data: dataSpecificReport, isFetching: isFetchingSpecificReport } =
    useGetReportSpecificFieldsQuery({
      type: reportType as REPORT_TYPES,
      report_id: Number(reportId),
    });

  console.log("aaa", dataSpecificReport);

  const { data: reportList, isFetching: isFetchingReportList } =
    useGetExternalShareQuery(reportParams);

  const { count, next, previous, results } = reportList || {};

  // const { getUserACLAccessLevel } = useACL();

  // columns
  const localStorageKey = localStorageKeys.columns.ReportsSharing(
    reportType ?? "",
    categoryId ?? "".toString(),
    reportId ?? "".toString()
  );
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKey);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setReportParams({ ...reportParams, search: value });
  }, 500);

  const filters: any[] = [
    {
      slug: "",
      title: t("filters.all"),
    },
    {
      slug: "1",
      title: t("filters.xls"),
    },
    {
      slug: "2",
      title: t("filters.csv"),
    },
    {
      slug: "3",
      title: t("filters.pdf"),
    },
  ];

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [reportList]);

  const activeFilterSlug = reportParams.external_share__format;

  const setActiveFilterSlug = (slug: string) => {
    setReportParams({
      ...reportParams,
      external_share__format: slug,
    });
  };

  const [deleteExternalShareEmail] = useDeleteExternalShareEmailMutation();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDeleteExternalShareEmail = async (id: number) => {
    try {
      setIsDeleting(true);
      await deleteExternalShareEmail(id).unwrap();
      setIsDeleting(false);

      // re-fetch the reports and update the store
      refetchCustomReportListToStore();
    } catch (error) {
      console.error("Failed to delete:", error);
      setIsDeleting(false);
    }
  };

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const [editItem, setEditItem] = React.useState<ShareReportListItem>();
  const showEditModal = (item: ShareReportListItem) => {
    setEditItem(item);
    dispatch(
      setModalsData({ ...modalsState, showShareViaEmailReportModal: true })
    );
  };

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as ShareReportListItem[])).map(
        (item: ShareReportListItem, index: number) => {
          return {
            navLink: ``,
            cellData: [
              item?.email,
              !!item?.external_share?.created_at && !!tzForDateFns
                ? formattedDateTimeBasedOnTimezone(
                    item?.external_share?.created_at,
                    tzForDateFns,
                    "LLL dd, yyyy - HH:mm"
                  )
                : "-",
              filters[item?.external_share?.format].title,
              item?.external_share?.send_automatically === true ? (
                <span className="text-field-success">{tMain("yes")}</span>
              ) : item?.external_share?.send_automatically === false ? (
                <span className="text-field-error-dark">{tMain("no")}</span>
              ) : (
                "-"
              ),
              <LastColumnElement
                value={
                  item?.external_share?.frequency
                    ? tEmail(
                        FREQUENCY_OPTIONS[item?.external_share?.frequency - 1]
                      )
                    : "-"
                }
                onEditClick={
                  item?.external_share?.send_automatically
                    ? () => {
                        showEditModal(item);
                      }
                    : undefined
                }
                onDeleteClick={
                  item?.external_share?.send_automatically
                    ? () => {
                        handleDeleteExternalShareEmail(item?.id);
                      }
                    : undefined
                }
              />,
            ].filter((_, index) => columns[index].show),
            isActive: item?.isactive === 1,
          };
        }
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView
        title={dataSpecificReport?.name}
        showBackButton
        rightComponent={
          <AdminFormFieldSubmit
            buttonDimensionClass={"h-10 w-full px-6 group"}
            customWrapperClass={`col-span-6`}
            type="button"
            variant="primary"
            label={
              <span className="flex justify-center whitespace-nowrap">
                {t("share")}
              </span>
            }
            onClick={() => {
              // display new share via email modal
              dispatch(
                setModalsData({
                  ...modalsState,
                  showShareViaEmailReportModal: true,
                })
              );
            }}
          />
        }
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ}`}>
        <ListingTableHeader
          activeFilterSlug={activeFilterSlug}
          filters={filters}
          heading={t("shared_with")}
          // heading={t("listing_heading")}
          searchBoxPlaceholder={t("search")}
          searchBoxValue={reportParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
          handleFilterChange={(slug) => setActiveFilterSlug(slug)}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKey}
        />
        <div className="py-4 mt-4 relative">
          {/* Show/Hide buttons are not needed on this page */}
          {/* <EditListingColumnsButton /> */}
          <AdminTable
            customTableHeadStyles="z-10"
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={
              isFetchingReportList || isDeleting || isFetchingSpecificReport
            }
            // listingQueryParams={reportParams}
            listingWrapperRef={listingWrapperRef}
          />
          {!isFetchingReportList && (
            <Pagination
              pageSize={reportParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                setReportParams({
                  ...reportParams,
                  page_size: parseInt(`${e?.value}`),
                });
              }}
              totalPages={count ? Math.ceil(count / reportParams.page_size) : 1}
              forcePage={reportParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                setReportParams({ ...reportParams, page: data?.selected + 1 });
              }}
              paginCurrentPage={reportParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {!!editItem && (
        <ModalShareViaEmail
          sharedReportItem={editItem}
          isEdit
          reportType={reportType}
          onClose={() => setEditItem(undefined)}
        />
      )}

      {/* (New) Share Via Email Modal */}
      {!editItem && modalsState.showShareViaEmailReportModal && (
        <ModalShareViaEmail
          reportType={reportType}
          reportId={parseInt(reportId as string)}
        />
      )}
    </>
  );
};

export default ScreenReportManageSharing;
